import ArtworkAtmosphere from "../components/Common/ArtworkAtmosphere";
// ~/apps/themusicchurch-app/frontend/src/pages/About.jsx

import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ValueScriptures from "../components/Common/ValueScriptures";
import MissionReflections from "../components/Common/MissionReflections";

const AboutWrapper = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.colors.homeNavy};
  color: ${p => p.theme.colors.white};
  --about-surface: #0d293b;
  --about-border: #24495a;
  --about-copy: #c6d9e2;
`;
const TitleSection = styled.section`
  padding: clamp(28px, 4vw, 48px) 24px 8px;
  text-align: center;
  background: radial-gradient(ellipse at 50% 0%, #104951, transparent 75%);
`;
const PageTitle = styled(motion.h1)`
  font: 700 clamp(2.8rem, 6vw, 4.5rem)/1.05 "DIN Condensed", "Arial Narrow", sans-serif;
  text-transform: uppercase;
  color: ${p => p.theme.colors.homeGold};
  letter-spacing: .03em;
  margin-bottom: 10px;
`;
const Subtitle = styled(motion.p)`
  font-size: clamp(.95rem, 2vw, 1.125rem);
  color: ${p => p.theme.colors.accent};
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;
`;
const ContentSection = styled.section`
  padding: 8px 24px 80px;
  max-width: 1120px;
  margin: 0 auto;
  @media (max-width: 600px) { padding: 8px 20px 56px; }
`;
const StorySection = styled.div`
  display: grid;
  grid-template-columns: var(--portrait-width, minmax(0, .8fr)) minmax(0, 1.2fr);
  gap: clamp(28px, 4vw, 48px);
  align-items: start;
  margin-bottom: clamp(32px, 4vw, 48px);
  @media (max-width: 900px) { grid-template-columns: minmax(0, 1fr); }
`;
const WelcomeArtwork = styled(ArtworkAtmosphere).attrs({ as: "figure" })`
  margin: 0;
  width: 100%;
  img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    border: 1px solid var(--about-border);
    border-radius: 4px;
  }
  @media (max-width: 900px) { max-width: 560px; margin: 0 auto; }
`;
const StoryCopy = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const IntroText = styled(motion.div)`
  max-width: 68ch;
  margin: 0 auto;
  padding-left: clamp(16px, 3vw, 28px);
  border-left: 2px solid ${p => p.theme.colors.accent};
  font-size: clamp(1rem, 1.5vw, 1.0625rem);
  line-height: 1.7;
  p { margin: 0; }
  color: ${p => p.theme.colors.white};
`;
const WhoWeAreSection = styled(motion.div)`
  margin-bottom: 0;
  padding: 20px;
  background: var(--about-surface);
  border: 1px solid var(--about-border);
  border-radius: 4px;
  h2 { margin-bottom: 16px; }
  p {
    font-size: clamp(1rem, 1.5vw, 1.0625rem);
    line-height: 1.7;
    color: var(--about-copy);
    margin-bottom: 16px;
  }
  p:last-child { margin-bottom: 0; }
`;
const SectionTitle = styled.h2`
  color: ${p => p.theme.colors.homeGold};
  font: 700 clamp(2rem, 4vw, 2.6rem)/1.1 "DIN Condensed", "Arial Narrow", sans-serif;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 12px;
  text-align: center;
`;
const TextContent = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.125rem);
  line-height: 1.9;
  color: var(--about-copy);
  max-width: 65ch;
  margin: 0 auto 24px;
`;
const MissionSlogans = styled(motion.div)`
  background: linear-gradient(135deg, #104951, #0d293b);
  border: 1px solid var(--about-border);
  padding: clamp(24px, 4vw, 40px);
  border-radius: 4px;
  margin-top: clamp(32px, 4vw, 48px);
`;
const WearMessageLink = styled(Link)`
  display: table;
  margin: 24px auto 0;
  padding: 10px 18px;
  border: 1px solid ${p => p.theme.colors.accent};
  border-radius: 4px;
  color: ${p => p.theme.colors.homeGold};
  font-size: .9rem;
  line-height: 1.5;
  text-decoration: none;
  &:hover { background: var(--about-surface); }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.homeGold}; outline-offset: 4px; }
`;

const BeliefStatement = styled(motion.section)`
  width: 100%;
  margin: clamp(32px, 4vw, 48px) 0 0;
  padding: clamp(24px, 4vw, 40px);
  background: #10404a;
  border: 1px solid ${p => p.theme.colors.homeGold};
  border-radius: 4px;
  p {
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(1.125rem, 2vw, 1.3rem);
    line-height: 1.75;
    color: ${p => p.theme.colors.white};
    text-align: left;
    max-width: 60ch;
  }
  p:first-child {
    font-family: "Arial Narrow", "Helvetica Neue", Arial, sans-serif;
    font-size: clamp(1.25rem, 2.2vw, 1.5rem);
    font-weight: 400;
    line-height: 1.5;
  }
  p:nth-last-child(2) { margin: 28px auto 8px; text-align: center; }
  p:last-child {
    margin-bottom: 0;
    color: ${p => p.theme.colors.white};
    font-family: ${p => p.theme.fonts.body};
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.9;
    text-align: center;
  }
`;

const FounderNote = styled.section`
  width: 100%;
  margin: clamp(32px, 4vw, 48px) 0 0;
  padding: clamp(24px, 4vw, 40px);
  background: var(--about-surface);
  border-radius: 4px;
  > div { max-width: 720px; margin: 0 auto; display: flow-root; }
  h2 {
    margin: 0 0 16px;
    color: ${p => p.theme.colors.homeGold};
    font: 700 clamp(1.5rem, 2.5vw, 1.7rem)/1.2 "DIN Condensed", "Arial Narrow", sans-serif;
    text-transform: uppercase;
    letter-spacing: .04em;
  }
  p {
    color: var(--about-copy);
    font-size: 1rem;
    line-height: 1.8;
    margin: 0 0 20px;
  }
  p:last-child { margin-bottom: 0; }
`;

const FounderPortrait = styled.img`
  float: left;
  display: block;
  width: 220px;
  max-width: 42%;
  height: auto;
  margin: 0 24px 16px 0;
  border: 0;
  -webkit-mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent), linear-gradient(to bottom, transparent, #000 4%, #000 92%, transparent);
  -webkit-mask-composite: source-in;
  mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent), linear-gradient(to bottom, transparent, #000 4%, #000 92%, transparent);
  mask-composite: intersect;
  @media (max-width: 480px) {
    float: none;
    width: 180px;
    max-width: 100%;
    margin: 0 auto 24px;
  }
`;

const About = () => {
  const storyRef = useRef(null);
  const copyRef = useRef(null);

  useLayoutEffect(() => {
    const story = storyRef.current;
    const copy = copyRef.current;
    let frame;
    let previousWidth = -1;
    const fitPortrait = () => {
      if (window.innerWidth <= 900) {
        story.style.removeProperty("--portrait-width");
        return;
      }
      const available = story.clientWidth - parseFloat(getComputedStyle(story).columnGap);
      // Measure the real text at bounded column widths; never compress or crop the image.
      let bestWidth = available * .4;
      let bestDifference = Infinity;
      for (let step = 0; step <= 56; step += 1) {
        const width = available * (.32 + step * .005);
        story.style.setProperty("--portrait-width", `${width}px`);
        const difference = Math.abs(copy.getBoundingClientRect().height - width * 1402 / 1122);
        if (difference < bestDifference) {
          bestDifference = difference;
          bestWidth = width;
        }
      }
      story.style.setProperty("--portrait-width", `${bestWidth}px`);
      story.dataset.alignmentDifference = bestDifference.toFixed(1);
    };
    const scheduleFit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fitPortrait);
    };
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width !== previousWidth) {
        previousWidth = entry.contentRect.width;
        scheduleFit();
      }
    });
    observer.observe(story);
    window.addEventListener("resize", scheduleFit);
    let active = true;
    document.fonts.ready.then(() => { if (active) scheduleFit(); });
    fitPortrait();
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", scheduleFit);
    };
  }, []);

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>About - The Music Church</title>
        <meta
          name="description"
          content="Learn about The Music Church - our mission, values, and the people who make up our community."
        />
      </Helmet>

      <AboutWrapper>
        {/* About page introduction */}
        <TitleSection>
          <PageTitle
            variants={titleVariants}
            initial="hidden"
            animate="visible"
          >
            Who We Are
          </PageTitle>
          <Subtitle
            variants={titleVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
          >
            A support group of imperfect people
          </Subtitle>
        </TitleSection>

        <ContentSection>
          <StorySection ref={storyRef}>
            <WelcomeArtwork>
              <img
                src="/images/about/who-we-are-2.png"
                alt="An older janitor and a young lawyer share a meal on a bench outside The Music Church, with a broom and briefcase beside them and people gathering nearby."
                width="1122"
                height="1402"
                decoding="async"
              />
            </WelcomeArtwork>
            <StoryCopy ref={copyRef}>
          <IntroText
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <p>
              We are a support group of imperfect people. Singers, musicians,
              chauffeurs, actors, preachers, dancers, janitors, pastors,
              teachers, entertainers, playwrights, plumbers, editors,
              electricians, directors, dishwashers and DJ's. Just regular folk,
              in all walks of life.
            </p>
          </IntroText>

          <WhoWeAreSection
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle>Our Foundation</SectionTitle>
            <TextContent>
              The least of us are the greatest among us and the greatest are the
              least. There are no titles, or worshipping of idols here. We care
              about each other, and share experiences in life, as we balance
              echoes that reverberate in our journey.
            </TextContent>
            <TextContent>
              In our walk to suppress dissonance that amplifies noise, to
              distract us from overcoming fears, failures and weaknesses. We
              recognize the war, so we put on the whole armor and fight, as we
              shine The Light.
            </TextContent>
            <TextContent>
              We relish the natural rhythm and harmonic magnetism of God's
              artistic science, the music of creation set into motion, all
              around us, the melody of time, calibrated in textures and tones,
              cataloging the growth of life, cultures past present and future.
            </TextContent>
          </WhoWeAreSection>
            </StoryCopy>
          </StorySection>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle>Our Values</SectionTitle>
            <TextContent style={{ textAlign: "center" }}>
              We believe in love, forgiveness, mercy, compassion and kindness. We believe goodness, faithfulness, patience, meekness, humility, generosity and honesty are attributes of lasting success.
            </TextContent>

            <ValueScriptures />
          </motion.div>

          <MissionSlogans
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle>Mission Slogans</SectionTitle>
            <MissionReflections />
            <WearMessageLink to="/store">Wear the Message</WearMessageLink>
          </MissionSlogans>

          <BeliefStatement
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <TextContent>
              We follow the amazing way, truth and life of our High Priest, Redeemer, Savior, Lord and returning Messiah, Jesus Christ. He is our model, rather than the political and socioeconomic traditions of today’s organized religions.
            </TextContent>
            <TextContent>At The Music Church, it’s always…</TextContent>
            <TextContent>
              Ministry Before Industry.
            </TextContent>
          </BeliefStatement>
          <FounderNote aria-labelledby="founder-note-title">
            <div>
            <h2 id="founder-note-title">A Note from the Founder</h2>
            <FounderPortrait
              src="/images/about/loris-holland-smiling-headphones.png"
              alt="Loris Holland smiling in the studio, wearing headphones beside a microphone."
              width="1111"
              height="1416"
              loading="lazy"
              decoding="async"
            />
            <p>
              I’m <Link to="/about/loris-holland" style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: "3px" }}>Loris Holland</Link>, servant and founder of The Music Church. I carry no title: pastor, bishop, prophet, evangelist or minister. We have no titles at The Music Church.
            </p>
            <p>
              I’m just an ordinary person with a conviction shaped by God’s faithfulness and merciful kindness towards me. I’ve learned through many ups and many downs, and I am still learning.
            </p>
            <p>
              My musical accomplishments have paved the way for me to learn much about people from all walks of life, across cultures and social backgrounds around the world, both within and beyond the church. Through salvation and God’s revelation, I’ve come to realize that we are, and always will be, our brother’s keeper. One hand washes the other. We need each other.
              <br />
              Welcome to The Music Church. We hope you can help us learn, and together we can do a little more with a shared purpose. And maybe we can help you too.
            </p>
            </div>
          </FounderNote>
        </ContentSection>
      </AboutWrapper>
    </>
  );
};

export default About;
